import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useClaims } from "../../../auth/ClaimsProvider";

/**
 * Check if we're on an internal path (embedded in Customer Success).
 * CS has its own UserFlow implementation, so we skip initialization here.
 */
function isInternalPath(pathname: string): boolean {
  return pathname.startsWith("/internal/");
}

interface Auth0User {
  email?: string;
  name?: string;
}

interface ClaimUserInfo {
  accountId?: string;
  email?: string;
  name?: string;
  impersonatorEmail?: string;
  isImpersonating: boolean;
}

async function initUserFlow(claimUserInfo: ClaimUserInfo, auth0User?: Auth0User) {
  // Dynamically import userflow only when needed
  const { default: userflow } = await import("userflow.js");

  userflow.init(import.meta.env.USERFLOW_TOKEN);

  // In the demo environment, use the actual authenticated user from Auth0
  // rather than the demo account from the user profile.
  // Double-check both DIST_ENV and hostname to ensure we're truly in demo.
  const isDemoEnv =
    import.meta.env.DIST_ENV === "demo" &&
    window.location.hostname === "demo.one.shi.com";

  // Determine userId, name, and email based on environment
  let userId: string;
  let name: string | undefined;
  let email: string | undefined;

  if (isDemoEnv && auth0User?.email) {
    // In demo, use Auth0 user's email as the unique identifier
    userId = auth0User.email;
    name = auth0User.name;
    email = auth0User.email;
  } else if (claimUserInfo.isImpersonating && claimUserInfo.impersonatorEmail) {
    // Standard logic for non-demo environments
    userId = claimUserInfo.impersonatorEmail;
    name = claimUserInfo.name;
    email = claimUserInfo.impersonatorEmail;
  } else {
    userId = claimUserInfo.email ?? auth0User?.email ?? "";
    name = claimUserInfo.name ?? auth0User?.name;
    email = claimUserInfo.email ?? auth0User?.email;
  }

  await userflow.identify(userId, {
    name,
    email,
    environment: import.meta.env.DIST_ENV,
    account_id: claimUserInfo.accountId,
    segment: undefined,
  });

  if (claimUserInfo.accountId != null) {
    await userflow.group(claimUserInfo.accountId);
  }
}

export function UserFlow() {
  const [userFlowInitialized, setUserFlowInitialized] = useState(false);
  const [dismounting, setDismounting] = useState(false);
  const location = useLocation();
  const { user } = useAuth0();
  const { claims, getClaimValue } = useClaims();

  const claimAxNumber = getClaimValue("axNumber") ?? "";
  const claimFirstName = getClaimValue("firstName") ?? "";
  const claimLastName = getClaimValue("lastName") ?? "";
  const claimEmail = getClaimValue("userEmail");
  const impersonatorEmail = getClaimValue("impersonatorEmail");
  const isImpersonating = getClaimValue("isImpersonating") === "true";

  useEffect(() => {
    // Skip UserFlow on internal paths - CS has its own implementation
    if (isInternalPath(location.pathname)) {
      return;
    }

    if (claims.length === 0) {
      return;
    }

    if (
      import.meta.env.USERFLOW_ENABLED &&
      import.meta.env.USERFLOW_TOKEN &&
      !userFlowInitialized
    ) {
      const claimFullName = `${claimFirstName} ${claimLastName}`.trim();
      const claimUserInfo: ClaimUserInfo = {
        accountId: claimAxNumber || undefined, // Using axNumber for accountId claim until the tenant ID is available
        email: claimEmail,
        name: claimFullName || undefined,
        impersonatorEmail,
        isImpersonating,
      };

      initUserFlow(claimUserInfo, user)
        .then(() => {
          if (dismounting) {
            return;
          }

          setUserFlowInitialized(true);
        })
        .catch(() => {});
    }

    return () => {
      setDismounting(true);
    };
  }, [
    claims,
    userFlowInitialized,
    dismounting,
    location.pathname,
    user,
    claimAxNumber,
    claimFirstName,
    claimLastName,
    claimEmail,
    impersonatorEmail,
    isImpersonating,
  ]);

  return <></>;
}
